import { Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { AuthStateService } from '@dental-ui/auth';
import {
  ACCOUNT_APP_DISPLAY_NAME,
  ACCOUNT_APP_NAME,
  CAD_APP_DISPLAY_NAME,
  CAD_APP_NAME,
  CLINIC_APP_DISPLAY_NAME,
  CLINIC_APP_NAME,
  LAB_APP_DISPLAY_NAME,
  LAB_APP_NAME,
  PORTAL_APP_DISPLAY_NAME,
  PORTAL_APP_NAME,
} from '@dental-ui/shared/consts';
import {
  LucideBox,
  LucideLayoutDashboard,
  LucideStethoscope,
  LucideUser,
} from '@lucide/angular';
import { filter, Subscription } from 'rxjs';

type RedirectCopy = {
  title: string;
  subtitle: string;
};

const OVERLAY_APP_LABEL: Record<string, string> = {
  [LAB_APP_NAME]: LAB_APP_DISPLAY_NAME,
  [CAD_APP_NAME]: CAD_APP_DISPLAY_NAME,
  [ACCOUNT_APP_NAME]: ACCOUNT_APP_DISPLAY_NAME,
  [CLINIC_APP_NAME]: CLINIC_APP_DISPLAY_NAME,
  [PORTAL_APP_NAME]: PORTAL_APP_DISPLAY_NAME,
};

const LOGOUT_COPY: Record<'tr' | 'en', RedirectCopy> = {
  tr: {
    title: 'Sizi yönlendiriyoruz',
    subtitle: 'Oturumunuz sonlandırılıyor, lütfen bekleyin…',
  },
  en: {
    title: 'Redirecting you',
    subtitle: 'Signing you out, please wait…',
  },
};

function overlayAppLabel(appName: string | null): string {
  return appName ? OVERLAY_APP_LABEL[appName] ?? '' : '';
}

function openingCopy(lang: 'tr' | 'en', appName: string | null): RedirectCopy {
  const label = overlayAppLabel(appName) || undefined;
  if (lang === 'tr') {
    return {
      title: label ? `Autobar ${label} açılıyor` : 'Autobar açılıyor',
      subtitle: 'Çalışma alanınıza yönlendiriliyorsunuz.',
    };
  }
  return {
    title: label ? `Opening Autobar ${label}` : 'Opening Autobar',
    subtitle: 'Taking you to your workspace.',
  };
}

@Component({
  selector: 'lib-auth-redirect-overlay',
  standalone: true,
  imports: [
    CommonModule,
    NgTemplateOutlet,
    LucideBox,
    LucideLayoutDashboard,
    LucideStethoscope,
    LucideUser,
  ],
  templateUrl: './auth-redirect-overlay.component.html',
  styleUrl: './auth-redirect-overlay.component.scss',
})
export class AuthRedirectOverlayComponent implements OnInit, OnDestroy {
  private readonly authState = inject(AuthStateService);
  private readonly router = inject(Router);
  private routerSubscription?: Subscription;

  readonly mode = this.authState.redirectOverlay;
  readonly sourceApp = computed(() => this.authState.redirectOverlaySourceApp());
  readonly destApp = computed(() => this.authState.redirectOverlayApp());
  readonly sourceLabel = computed(() => overlayAppLabel(this.sourceApp()));
  readonly destLabel = computed(() => overlayAppLabel(this.destApp()));

  readonly copy = computed(() => {
    const currentMode = this.mode();
    if (!currentMode) {
      return null;
    }

    const lang = this.authState.getRedirectLanguage();
    if (currentMode === 'logout') {
      return LOGOUT_COPY[lang];
    }

    return openingCopy(lang, this.destApp());
  });

  ngOnInit(): void {
    // Cross-app login uses window.location — keep overlay until unload.
    // Do not clear on NavigationCancel (loginGuard false after navigateAfterLogin).
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const mode = this.mode();
        if (!mode) {
          return;
        }

        const url = event.urlAfterRedirects || event.url;
        const onAuthRoute =
          url.includes('/login') ||
          url.includes('/register') ||
          url.includes('/forget-password');

        if (mode === 'logout') {
          // Logout destination is the login page — clear overlay so the form shows.
          if (onAuthRoute) {
            this.authState.hideRedirectOverlay();
          }
          return;
        }

        if (mode === 'login' && onAuthRoute) {
          // Still on login while waiting for cross-app handoff — keep covered.
          return;
        }

        this.authState.hideRedirectOverlay();
      });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }
}
